import { defineStore } from 'pinia'
import { useMutation } from '@tanstack/vue-query'
import api, {
  noAuthApi,
  markSessionEstablished,
  clearSessionFlag,
  withoutOtherRequests,
  isSessionEstablished,
  registerSessionExpiredCallback
} from '@dashboard/services/axiosApi'
import { isAxiosError } from 'axios'
import { md5 } from 'js-md5'
import type { UpdatePassword, UpdateUser, User } from '@dashboard/types/user'
import { useNotificationStore } from './notificationStore'
import { usePostHog } from '@dashboard/composables/usePosthog'
import { recordAnalyticsEvent, AnalyticsEventTypes } from '@dashboard/services/analytics'
import { ROUTES } from '@dashboard/router/routeNames'
import { safeInternalRedirect } from '@dashboard/router/safeRedirect'
import { useConfigStore } from '@dashboard/stores/configStore'
import { useTeamStore } from '@dashboard/stores/teamStore'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()
  const route = useRoute()
  const { resetUser } = usePostHog()
  const user = ref<User | null>(null)
  const isAuthenticated = ref(false)

  // Register callback so axiosApi can clear auth state on 401 without
  // a circular dependency. This ensures the login route guard sees
  // isAuthenticated=false and shows the login form.
  registerSessionExpiredCallback(() => {
    user.value = null
    isAuthenticated.value = false
  })

  const listenForErrorBroadcast = (id: User['id']) => {
    window.Echo?.private(`error-exception.${id}`).listen(
      'GeneralExceptionBroadcaster',
      (data: { message?: string }) => {
        if (data.message) {
          const notificationStore = useNotificationStore()
          notificationStore.addError(data.message)
        }
      }
    )
  }
  const getUser = async (force = false): Promise<User | null> => {
    if (user.value && !force) {
      listenForErrorBroadcast(user.value.id)
      return user.value
    }
    // On first load (no established session), refresh the CSRF cookie before
    // hitting /api/user. This ensures cross-subdomain navigation (e.g., admin
    // going from Filament to the webapp) has a fresh XSRF-TOKEN. Without this,
    // the request can fail with 419 (token mismatch) which surfaces as a 401.
    if (!isSessionEstablished()) {
      await api.get('/sanctum/csrf-cookie')
    }
    const { data: loadedUser } = await api.get<User>('/api/user')
    user.value = loadedUser
    if (loadedUser.id !== undefined) {
      listenForErrorBroadcast(loadedUser.id)
    }
    if (loadedUser.trial_ends_at) {
      loadedUser.trial_ends_at = new Date(loadedUser.trial_ends_at)
    }
    if (window.Ferndesk) {
      window.Ferndesk('prefill', {
        email: loadedUser.email,
        ...(loadedUser.name && { name: loadedUser.name })
      })
    }
    void api.get('/api/user_cookie')
    isAuthenticated.value = true
    markSessionEstablished()
    return loadedUser
  }

  const validatedRedirectPath = computed(() => safeInternalRedirect(route.query.redirect))

  const postLogin = (options: { destination?: string; ignoreRedirect?: boolean } = {}): Promise<void> => {
    // `/` resolves a team. Honoring a redirect while that navigation is in
    // progress reads the login route, which still has the redirect, and pushes
    // the same URL again.
    if (options.destination === undefined && !options.ignoreRedirect && validatedRedirectPath.value) {
      return Promise.resolve(router.push(validatedRedirectPath.value)).then(() => undefined)
    }

    return new Promise((resolve, reject) => {
      getUser()
        .then(async () => {
          // Track login event
          void recordAnalyticsEvent(AnalyticsEventTypes.LOGGED_IN)

          const teamStore = useTeamStore()
          const { teams, isSuccess, isError } = storeToRefs(teamStore)
          const destination = options.destination ?? ROUTES.DASHBOARD
          const destinationPath = destination === ROUTES.ONBOARDING ? 'onboarding' : 'dashboard'

          const handleRedirect = () => {
            // When coming from demo mode (registration page), do a full page
            // reload to shed all demo state (cached config, isDemo flags, Echo
            // channels, TanStack Query cache). Mirrors TeamDropdown behavior.
            const configStore = useConfigStore()
            const { currentTeamPid } = storeToRefs(configStore)
            const fromDemo = currentTeamPid.value === 'demo'

            const firstTeam = teams.value?.[0]
            if (firstTeam !== undefined) {
              if (fromDemo) {
                window.location.href = `/${firstTeam.pid}/${destinationPath}`
              } else {
                void router.push({
                  name: destination,
                  params: { teamPid: firstTeam.pid }
                })
              }
            } else {
              void router.push({ name: ROUTES.SETUPBLANKUSER })
            }
          }

          if (isError.value) {
            reject(new Error('Failed to load projects'))
            return
          }

          // If teams query already resolved, redirect immediately
          if (isSuccess.value) {
            handleRedirect()
            resolve()
            return
          }

          // Otherwise wait for the teams query to finish
          const stopWatch = watch([isSuccess, isError], ([success, failed]) => {
            if (!success && !failed) {
              return
            }
            stopWatch()
            if (failed) {
              reject(new Error('Failed to load projects'))
              return
            }
            handleRedirect()
            resolve()
          })
        })
        .catch((error: unknown) => {
          console.error('Failed to load user after login:', error)
          useNotificationStore().addError('We could not load your account. Please refresh the page and try again.')
          reject(error instanceof Error ? error : new Error('Failed to load your account'))
        })
    })
  }

  const loginMutation = useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      await api.get('/sanctum/csrf-cookie')
      return api.post<{ two_factor?: boolean }>('/login', credentials)
    },
    onSuccess: ({ data }) => {
      // A two-factor account is not signed in yet: the login page asks for a code
      // and finishes through `twoFactorChallenge`.
      if (data.two_factor === true) {
        return
      }
      void postLogin().catch(() => {
        // postLogin already surfaces the failure. Swallow it here so the mutation
        // does not raise an unhandled rejection.
      })
    }
  })

  /** Finishes a sign-in that `POST /login` answered with `{ two_factor: true }`. */
  const twoFactorChallenge = async (payload: { code: string } | { recovery_code: string }): Promise<void> => {
    await api.post('/two-factor-challenge', payload)
  }

  const forgotPasswordMutation = useMutation({
    mutationFn: async (credentials: { email: string }) => {
      await api.get('/sanctum/csrf-cookie')
      return api.post('/forgot-password', credentials)
    }
  })
  const resetPasswordMutation = useMutation({
    mutationFn: async (credentials: { email: string; password: string; confirmPassword: string; token: string }) => {
      await api.get('/sanctum/csrf-cookie')
      return api.post('/reset-password', {
        email: credentials.email,
        password: credentials.password,
        password_confirmation: credentials.confirmPassword,
        token: credentials.token
      })
    }
  })

  const login = () => {
    const { isError, error, isPending, mutate } = loginMutation
    return { isPending, error, isError, mutate }
  }

  const forgotPassword = () => {
    const { isError, error, isPending, mutate, mutateAsync } = forgotPasswordMutation
    return { isPending, error, isError, mutate, mutateAsync }
  }

  const resetPassword = () => {
    const { isError, error, isPending, mutate, mutateAsync } = resetPasswordMutation
    return { isPending, error, isError, mutate, mutateAsync }
  }

  /** Forget the signed-in user locally, after logout or once the server has ended the session. */
  const clearSession = () => {
    resetUser()
    user.value = null
    isAuthenticated.value = false
    clearSessionFlag()
  }

  const logoutMutation = useMutation({
    mutationFn: () => api.post('/logout', undefined, { duringSignOut: true }),
    onSuccess: () => {
      void recordAnalyticsEvent(AnalyticsEventTypes.LOGGED_OUT)
      clearSession()
    }
  })

  /**
   * Ends the session on the server, then here, aborting this page's other requests
   * and holding new ones until it's done. Resolves false when the session is still signed in.
   */
  const logout = async (): Promise<boolean> => {
    try {
      await withoutOtherRequests(() => logoutMutation.mutateAsync())
      return true
    } catch (error) {
      // A 401 means the session had already ended.
      if (isAxiosError(error) && error.response?.status === 401) {
        clearSession()
        return true
      }
      return false
    }
  }

  /**
   * Saves name and email. A changed address (ignoring case) comes back
   * unconfirmed, as the server clears its verification; the user is then
   * reloaded in the background.
   */
  const updateProfile = async (data: UpdateUser): Promise<void> => {
    await api.put('/user/profile-information', data)
    if (user.value) {
      const emailChanged = user.value.email.toLowerCase() !== data.email.toLowerCase()
      user.value = {
        ...user.value,
        ...data,
        email_verified_at: emailChanged ? null : user.value.email_verified_at
      }
    }
    void getUser(true).catch(() => undefined)
  }

  const updatePassword = async (data: UpdatePassword): Promise<void> => {
    await api.put('/user/password', { ...data, password_confirmation: data.password })
  }

  const activateAccountMutation = useMutation({
    mutationFn: async (variables: { hash: string; email: string; password: string }) => {
      await api.get('/sanctum/csrf-cookie')
      return api.post(`/api/invitations/${variables.hash}`, {
        email: variables.email,
        password: variables.password
      })
    },
    onSuccess: async ({ data }, variables) => {
      if (data.requiresLogin) {
        // Already signed in as the invited account: go straight to the project.
        if (isAuthenticated.value && user.value?.email?.toLowerCase() === variables.email.trim().toLowerCase()) {
          await router.push({ name: ROUTES.DASHBOARD, params: { teamPid: data.teamPid } })
          return
        }
        // Signed in as someone else: end that session so the invitee can sign in (with two-factor).
        if (isAuthenticated.value) {
          await logout()
        }
        await router.push({
          name: ROUTES.LOGIN,
          query: {
            redirect: `/${data.teamPid}/dashboard`,
            invitationAccepted: 'true'
          }
        })
        return
      }
      if (data.teamPid) {
        void router.push({
          name: ROUTES.DASHBOARD,
          params: { teamPid: data.teamPid }
        })
      }
    }
  })

  const activateAccount = (variables: { hash: string; email: string; password: string }) =>
    activateAccountMutation.mutate(variables)

  const profilePhotoUrl = computed(() => {
    if (!user.value || !user.value.email) {
      return ''
    }
    const hash = md5(user.value.email.toLowerCase().trim())
    return `https://www.gravatar.com/avatar/${hash}?s=200&d=mp`
  })

  const isAdmin = computed(() => user.value?.is_admin)

  const hasPermission = (permission: string): boolean => {
    // Admins have all permissions
    if (isAdmin.value) {
      return true
    }

    // Check if user has the specific permission
    return user.value?.effective_permissions?.includes(permission) || false
  }

  const hasRole = (role: string): boolean => user.value?.roles?.includes(role) || false

  const canCopyFlow = computed(() => hasPermission('flow.copy'))
  const canLoadFlow = computed(() => hasPermission('flow.load'))

  return {
    user,
    isAuthenticated,
    getUser,
    postLogin,
    login,
    twoFactorChallenge,
    logout,
    clearSession,
    resetPassword,
    forgotPassword,
    updateProfile,
    updatePassword,
    activateAccount,
    profilePhotoUrl,
    isAdmin,
    hasPermission,
    hasRole,
    canCopyFlow,
    canLoadFlow
  }
})
